import _React, { useState, useEffect } from 'react';
import { getServerSessionId } from '../../lib/serverSession';
import { logAction } from '../../lib/personalActivityLogger';

interface VerificationResult {
  user_id: number;
  verification_type: string;
  total_issues: number;
  issues: any[];
  verified_at: string;
  summary?: any;
}

interface AuditResult {
  user_id: number;
  total_issues: number;
  purchases: VerificationResult;
  transfers: VerificationResult;
  bonuses: VerificationResult;
  conversions: VerificationResult;
  multilevel_commissions: VerificationResult;
  referral_commissions: VerificationResult;
  sponsorship_commissions: VerificationResult;
  verified_at: string;
}

interface UserInfo {
  user_id: number;
  username: string;
  email: string;
  full_name: string;
  created_at: string;
  sponsor_id: number | null;
  sponsor_username: string | null;
}

interface ComprehensiveAudit {
  user_info: UserInfo;
  purchases: { records: any[]; total_shares: number; total_bonus: number };
  transfers: { records: any[]; total_received: number; total_sent: number };
  conversions: { records: any[]; total_shares: number };
  presale_commissions: { records: any[]; total_usdt: number; total_shares: number };
  multilevel_commissions: { records: any[]; total_usdt: number; total_shares: number };
  balance_check: {
    commission_balances_record: { usdt_balance: number; share_balance: number; total_earned_usdt: number; total_earned_shares: number };
    calculated_totals: any;
    discrepancies: { usdt_earned_diff: number; shares_earned_diff: number; has_usdt_discrepancy: boolean; has_shares_discrepancy: boolean };
  };
  audit_timestamp: string;
}

interface Props {
  userId: number;
  username: string;
  onClose: () => void;
  onRecordEdit?: (recordType: string, record: any) => void;
}

export function UserAuditPanel({ userId, username, onClose, onRecordEdit }: Props) {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [comprehensiveAudit, setComprehensiveAudit] = useState<ComprehensiveAudit | null>(null);
  const [verificationResults, setVerificationResults] = useState<Record<string, VerificationResult>>({});
  const [fullAuditResult, setFullAuditResult] = useState<AuditResult | null>(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'purchases' | 'transfers' | 'conversions' | 'presale' | 'multilevel' | 'issues'>('overview');
  const [expandedIssue, setExpandedIssue] = useState<string | null>(null);

  // v4.7.53 — Personal Activity: record which user this panel is auditing
  useEffect(() => {
    logAction('user_edit', 'open_user_audit_panel', {
      target_type: 'user',
      target_id: String(userId),
      target_label: username || `User ${userId}`
    });
  }, [userId, username]);

  const loadComprehensiveAudit = async () => {
    try {
      setLoading(true);
      setError('');
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/user-audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_comprehensive_audit', data: { userId } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setComprehensiveAudit(result.audit);
    } catch (err) {
      console.error('Error loading comprehensive audit:', err);
      setError(err instanceof Error ? err.message : 'Failed to load audit');
    } finally {
      setLoading(false);
    }
  };

  const runVerification = async (verificationType: string) => {
    try {
      setVerifying(verificationType);
      setError('');
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/user-audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'run_verification', data: { userId, verificationType } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setVerificationResults(prev => ({ ...prev, [verificationType]: result.verification }));
    } catch (err) {
      console.error(`Error running ${verificationType} verification:`, err);
      setError(err instanceof Error ? err.message : `Failed to verify ${verificationType}`);
    } finally {
      setVerifying(null);
    }
  };

  const runFullAudit = async () => {
    try {
      setVerifying('all');
      setError('');
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/user-audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'run_full_audit', data: { userId } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setFullAuditResult(result.audit);
      setActiveTab('issues');
    } catch (err) {
      console.error('Error running full audit:', err);
      setError(err instanceof Error ? err.message : 'Failed to run full audit');
    } finally {
      setVerifying(null);
    }
  };

  const getIssueColor = (count: number) => {
    if (count === 0) return '#86EFAC';
    if (count < 5) return '#FCD34D';
    return '#FCA5A5';
  };

  const verificationButtons = [
    { key: 'purchases', label: '📦 Purchases', desc: 'Verify share calculations' },
    { key: 'transfers', label: '🔄 Transfers', desc: 'Verify transfer records' },
    { key: 'bonuses', label: '🎁 Bonuses', desc: 'Verify bonus tier calculations' },
    { key: 'conversions', label: '💱 Conversions', desc: 'Verify USDT to share conversions' },
    { key: 'multilevel_commissions', label: '📊 Multi-Level', desc: 'Verify ML commission cascade' },
    { key: 'referral_commissions', label: '👥 Referral', desc: 'Verify Pre-Sale 15%+15%' },
    { key: 'sponsorship_commissions', label: '🤝 Sponsorship', desc: 'Verify sponsor received commission' },
  ];

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1000, padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ maxWidth: '1400px', width: '100%', maxHeight: '90vh', overflowY: 'auto', backgroundColor: '#1F2937', borderRadius: '12px', border: '1px solid #374151' }}>
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid #374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#FFFFFF', margin: 0, fontSize: '24px' }}>🔍 User Audit: {username}</h2>
            <p style={{ color: '#ffffff', margin: '4px 0 0 0', fontSize: '14px' }}>User ID: {userId}</p>
          </div>
          <button onClick={onClose} style={{ backgroundColor: '#374151', color: '#FFFFFF', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer' }}>✕ Close</button>
        </div>

        {error && <div style={{ margin: '16px', padding: '12px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '6px' }}>{error}</div>}

        {/* Verification Buttons */}
        <div style={{ padding: '20px', borderBottom: '1px solid #374151' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <button onClick={loadComprehensiveAudit} disabled={loading} style={{ padding: '10px 16px', backgroundColor: '#3B82F6', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
              {loading ? '⏳ Loading...' : '📊 Load Full Data'}
            </button>
            <button onClick={runFullAudit} disabled={verifying === 'all'} style={{ padding: '10px 16px', backgroundColor: '#10B981', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', opacity: verifying === 'all' ? 0.6 : 1 }}>
              {verifying === 'all' ? '⏳ Verifying...' : '✅ Run Full Audit'}
            </button>
          </div>
          <p style={{ color: '#ffffff', fontSize: '14px', marginBottom: '12px' }}>Individual Verification Checks:</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
            {verificationButtons.map(btn => {
              const result = verificationResults[btn.key];
              const isVerifying = verifying === btn.key;
              return (
                <button key={btn.key} onClick={() => runVerification(btn.key)} disabled={isVerifying} style={{ padding: '12px', backgroundColor: result ? (result.total_issues === 0 ? '#065F46' : '#7F1D1D') : '#374151', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', textAlign: 'left', opacity: isVerifying ? 0.6 : 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '13px' }}>{btn.label}</div>
                  <div style={{ fontSize: '13px', color: '#ffffff', marginTop: '2px' }}>{btn.desc}</div>
                  {result && <div style={{ marginTop: '4px', fontSize: '14px', color: getIssueColor(result.total_issues) }}>{result.total_issues} issues</div>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ padding: '0 20px', borderBottom: '1px solid #374151', display: 'flex', gap: '4px', overflowX: 'auto' }}>
          {(['overview', 'purchases', 'transfers', 'conversions', 'presale', 'multilevel', 'issues'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '12px 16px', backgroundColor: activeTab === tab ? '#3B82F6' : 'transparent', color: activeTab === tab ? '#FFFFFF' : '#9CA3AF', border: 'none', borderRadius: '6px 6px 0 0', cursor: 'pointer', fontWeight: activeTab === tab ? '600' : '400', whiteSpace: 'nowrap' }}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'issues' && fullAuditResult && <span style={{ marginLeft: '6px', backgroundColor: fullAuditResult.total_issues > 0 ? '#EF4444' : '#10B981', padding: '2px 6px', borderRadius: '10px', fontSize: '13px' }}>{fullAuditResult.total_issues}</span>}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '20px', minHeight: '400px', maxHeight: '60vh', overflowY: 'auto' }}>
          {activeTab === 'overview' && comprehensiveAudit && (
            <OverviewTab audit={comprehensiveAudit} />
          )}
          {activeTab === 'purchases' && comprehensiveAudit && (
            <RecordsTable title="Share Purchases" records={comprehensiveAudit.purchases.records} columns={['date', 'package_name', 'shares_purchased', 'bonus_shares', 'total_amount', 'price_per_share', 'phase_id', 'status']} onEdit={onRecordEdit ? (r) => onRecordEdit('purchase', r) : undefined} />
          )}
          {activeTab === 'transfers' && comprehensiveAudit && (
            <RecordsTable title="Share Transfers" records={comprehensiveAudit.transfers.records} columns={['date', 'type', 'shares', 'other_username', 'reason', 'status']} onEdit={onRecordEdit ? (r) => onRecordEdit('transfer', r) : undefined} />
          )}
          {activeTab === 'conversions' && comprehensiveAudit && (
            <RecordsTable title="Commission Conversions" records={comprehensiveAudit.conversions.records} columns={['date', 'shares_requested', 'usdt_amount', 'share_price', 'phase_id', 'status']} onEdit={onRecordEdit ? (r) => onRecordEdit('conversion', r) : undefined} />
          )}
          {activeTab === 'presale' && comprehensiveAudit && (
            <RecordsTable title="Pre-Sale Commissions (15%+15%)" records={comprehensiveAudit.presale_commissions.records} columns={['date', 'referred_username', 'purchase_amount', 'usdt_commission', 'share_commission', 'status']} onEdit={onRecordEdit ? (r) => onRecordEdit('presale_commission', r) : undefined} />
          )}
          {activeTab === 'multilevel' && comprehensiveAudit && (
            <RecordsTable title="Multi-Level Commissions (10%+5%)" records={comprehensiveAudit.multilevel_commissions.records} columns={['date', 'level', 'referred_username', 'share_purchase_amount', 'usdt_commission', 'shares_purchased', 'status']} onEdit={onRecordEdit ? (r) => onRecordEdit('multilevel_commission', r) : undefined} />
          )}
          {activeTab === 'issues' && fullAuditResult && (
            <IssuesTab audit={fullAuditResult} expandedIssue={expandedIssue} setExpandedIssue={setExpandedIssue} />
          )}
          {!comprehensiveAudit && activeTab !== 'issues' && (
            <div style={{ textAlign: 'center', color: '#ffffff', padding: '40px' }}>
              <p>Click "Load Full Data" to view user transaction data</p>
            </div>
          )}
          {activeTab === 'issues' && !fullAuditResult && (
            <div style={{ textAlign: 'center', color: '#ffffff', padding: '40px' }}>
              <p>Click "Run Full Audit" to check for discrepancies</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ audit }: { audit: ComprehensiveAudit }) {
  const { user_info, balance_check, purchases, transfers, conversions, presale_commissions, multilevel_commissions } = audit;
  const disc = balance_check.discrepancies;

  return (
    <div>
      {/* User Info */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ color: '#FFFFFF', marginBottom: '12px' }}>👤 User Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <InfoCard label="Username" value={user_info.username} />
          <InfoCard label="Email" value={user_info.email} />
          <InfoCard label="Full Name" value={user_info.full_name || '-'} />
          <InfoCard label="Sponsor" value={user_info.sponsor_username || 'AUREUS (Master)'} />
          <InfoCard label="Joined" value={new Date(user_info.created_at).toLocaleDateString()} />
        </div>
      </div>

      {/* Balance Check */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ color: '#FFFFFF', marginBottom: '12px' }}>💰 Commission Balance Check</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <InfoCard label="USDT Balance" value={`$${balance_check.commission_balances_record.usdt_balance.toFixed(2)}`} />
          <InfoCard label="Share Balance" value={balance_check.commission_balances_record.share_balance.toFixed(4)} />
          <InfoCard label="Total Earned USDT" value={`$${balance_check.commission_balances_record.total_earned_usdt.toFixed(2)}`} />
          <InfoCard label="Total Earned Shares" value={balance_check.commission_balances_record.total_earned_shares.toFixed(4)} />
        </div>
        {(disc.has_usdt_discrepancy || disc.has_shares_discrepancy) && (
          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#7F1D1D', borderRadius: '6px' }}>
            <p style={{ color: '#FCA5A5', fontWeight: '600', margin: '0 0 8px 0' }}>⚠️ Discrepancies Detected:</p>
            {disc.has_usdt_discrepancy && <p style={{ color: '#FFFFFF', margin: '4px 0', fontSize: '14px' }}>USDT Difference: ${disc.usdt_earned_diff.toFixed(2)}</p>}
            {disc.has_shares_discrepancy && <p style={{ color: '#FFFFFF', margin: '4px 0', fontSize: '14px' }}>Shares Difference: {disc.shares_earned_diff.toFixed(4)}</p>}
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div>
        <h3 style={{ color: '#FFFFFF', marginBottom: '12px' }}>📊 Transaction Summary</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
          <InfoCard label="Purchased Shares" value={purchases.total_shares.toFixed(2)} />
          <InfoCard label="Bonus Shares" value={purchases.total_bonus.toFixed(2)} />
          <InfoCard label="Received Transfers" value={transfers.total_received.toFixed(2)} />
          <InfoCard label="Sent Transfers" value={transfers.total_sent.toFixed(2)} />
          <InfoCard label="Conversion Shares" value={conversions.total_shares.toFixed(2)} />
          <InfoCard label="Pre-Sale USDT" value={`$${presale_commissions.total_usdt.toFixed(2)}`} />
          <InfoCard label="Pre-Sale Shares" value={presale_commissions.total_shares.toFixed(4)} />
          <InfoCard label="ML USDT" value={`$${multilevel_commissions.total_usdt.toFixed(2)}`} />
          <InfoCard label="ML Shares" value={multilevel_commissions.total_shares.toFixed(4)} />
        </div>
      </div>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px' }}>
      <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>{label}</p>
      <p style={{ color: '#FFFFFF', fontSize: '14px', margin: 0, fontWeight: '500' }}>{value}</p>
    </div>
  );
}

function RecordsTable({ title, records, columns, onEdit }: { title: string; records: any[]; columns: string[]; onEdit?: (record: any) => void }) {
  if (!records || records.length === 0) {
    return <div style={{ color: '#ffffff', textAlign: 'center', padding: '40px' }}>No {title.toLowerCase()} found</div>;
  }

  const formatValue = (key: string, value: any) => {
    if (value === null || value === undefined) return '-';
    if (key.includes('date') || key === 'date') return new Date(value).toLocaleString();
    if (key.includes('amount') || key.includes('usdt') || key.includes('price')) return typeof value === 'number' ? `$${value.toFixed(2)}` : value;
    if (key.includes('shares') || key.includes('bonus')) return typeof value === 'number' ? value.toFixed(4) : value;
    return String(value);
  };

  return (
    <div>
      <h3 style={{ color: '#FFFFFF', marginBottom: '12px' }}>{title} ({records.length})</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#374151' }}>
              {columns.map(col => <th key={col} style={{ padding: '10px 8px', textAlign: 'left', color: '#ffffff', fontWeight: '600', textTransform: 'capitalize' }}>{col.replace(/_/g, ' ')}</th>)}
              {onEdit && <th style={{ padding: '10px 8px', textAlign: 'center', color: '#ffffff' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {records.map((record, idx) => (
              <tr key={record.id || idx} style={{ borderBottom: '1px solid #374151' }}>
                {columns.map(col => <td key={col} style={{ padding: '10px 8px', color: '#FFFFFF' }}>{formatValue(col, record[col])}</td>)}
                {onEdit && <td style={{ padding: '10px 8px', textAlign: 'center' }}><button onClick={() => onEdit(record)} style={{ backgroundColor: '#3B82F6', color: '#FFFFFF', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '13px' }}>Edit</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IssuesTab({ audit, expandedIssue, setExpandedIssue }: { audit: AuditResult; expandedIssue: string | null; setExpandedIssue: (id: string | null) => void }) {
  const categories = [
    { key: 'purchases', label: '📦 Purchases', data: audit.purchases },
    { key: 'transfers', label: '🔄 Transfers', data: audit.transfers },
    { key: 'bonuses', label: '🎁 Bonuses', data: audit.bonuses },
    { key: 'conversions', label: '💱 Conversions', data: audit.conversions },
    { key: 'multilevel_commissions', label: '📊 Multi-Level', data: audit.multilevel_commissions },
    { key: 'referral_commissions', label: '👥 Referral', data: audit.referral_commissions },
    { key: 'sponsorship_commissions', label: '🤝 Sponsorship', data: audit.sponsorship_commissions },
  ];

  return (
    <div>
      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: audit.total_issues > 0 ? '#7F1D1D' : '#065F46', borderRadius: '6px' }}>
        <p style={{ color: '#FFFFFF', margin: 0, fontWeight: '600' }}>
          {audit.total_issues > 0 ? `⚠️ ${audit.total_issues} Total Issues Found` : '✅ No Issues Found - All Verified'}
        </p>
      </div>

      {categories.map(cat => (
        <div key={cat.key} style={{ marginBottom: '12px', backgroundColor: '#111827', borderRadius: '6px', overflow: 'hidden' }}>
          <button onClick={() => setExpandedIssue(expandedIssue === cat.key ? null : cat.key)} style={{ width: '100%', padding: '12px 16px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#FFFFFF', fontWeight: '500' }}>{cat.label}</span>
            <span style={{ color: cat.data.total_issues === 0 ? '#86EFAC' : '#FCA5A5', fontSize: '14px' }}>{cat.data.total_issues} issues {expandedIssue === cat.key ? '▲' : '▼'}</span>
          </button>
          {expandedIssue === cat.key && cat.data.issues.length > 0 && (
            <div style={{ padding: '0 16px 16px', maxHeight: '300px', overflowY: 'auto' }}>
              {cat.data.issues.map((issue: any, idx: number) => (
                <div key={idx} style={{ padding: '10px', backgroundColor: '#1F2937', borderRadius: '4px', marginBottom: '8px', fontSize: '14px' }}>
                  <p style={{ color: '#FCA5A5', fontWeight: '600', margin: '0 0 6px 0' }}>{issue.issue_type}</p>
                  <pre style={{ color: '#ffffff', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(issue, null, 2)}</pre>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

